// Frontend caching utility for BRSR repository data
// Uses localStorage with TTL support

export const BrsrCache = {
  // Cache key prefixes
  PREFIXES: {
    REPOSITORY_LIST: "brsr_repo_list_",
    REPOSITORY_ITEM: "brsr_repo_item_",
    REPORT_METADATA: "brsr_metadata_",
    LINKS_DATA: "brsr_links_",
  },

  // TTL in milliseconds
  TTL: {
    REPOSITORY_LIST: 5 * 60 * 1000, // 5 minutes
    REPOSITORY_ITEM: 10 * 60 * 1000, // 10 minutes
    LINKS_DATA: 5 * 60 * 1000, // 5 minutes
  },

  /**
   * Set cache item with TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds
   */
  set: (key, value, ttl = BrsrCache.TTL.REPOSITORY_ITEM) => {
    try {
      const cacheEntry = {
        value,
        expiresAt: Date.now() + ttl,
        createdAt: Date.now(),
      };
      localStorage.setItem(key, JSON.stringify(cacheEntry));
      return true;
    } catch (error) {
      console.error("Cache set error:", error);
      return false;
    }
  },

  /**
   * Get cache item if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if expired/missing
   */
  get: (key) => {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;

      const cacheEntry = JSON.parse(cached);
      
      // Check if expired
      if (Date.now() > cacheEntry.expiresAt) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheEntry.value;
    } catch (error) {
      console.error("Cache get error:", error);
      return null;
    }
  },

  /**
   * Remove cache item
   * @param {string} key - Cache key
   */
  remove: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error("Cache remove error:", error);
      return false;
    }
  },

  /**
   * Clear all BRSR cache
   */
  clearAll: () => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith("brsr_")) {
          localStorage.removeItem(key);
        }
      });
      return true;
    } catch (error) {
      console.error("Cache clear error:", error);
      return false;
    }
  },

  /**
   * Cache repository list
   * @param {string} companyId - Company ID
   * @param {any} data - Repository data
   */
  setRepositoryList: (companyId, data) => {
    const key = BrsrCache.PREFIXES.REPOSITORY_LIST + companyId;
    return BrsrCache.set(key, data, BrsrCache.TTL.REPOSITORY_LIST);
  },

  /**
   * Get cached repository list
   * @param {string} companyId - Company ID
   * @returns {any|null}
   */
  getRepositoryList: (companyId) => {
    const key = BrsrCache.PREFIXES.REPOSITORY_LIST + companyId;
    return BrsrCache.get(key);
  },

  /**
   * Cache links data
   * @param {string} reportId - Report ID
   * @param {any} links - Links data
   */
  setLinks: (reportId, links) => {
    const key = BrsrCache.PREFIXES.LINKS_DATA + reportId;
    return BrsrCache.set(key, links, BrsrCache.TTL.LINKS_DATA);
  },

  /**
   * Get cached links
   * @param {string} reportId - Report ID
   * @returns {any|null}
   */
  getLinks: (reportId) => {
    const key = BrsrCache.PREFIXES.LINKS_DATA + reportId;
    return BrsrCache.get(key);
  },

  /**
   * Invalidate repository cache for a company
   * @param {string} companyId - Company ID
   */
  invalidateRepositoryCache: (companyId) => {
    BrsrCache.remove(BrsrCache.PREFIXES.REPOSITORY_LIST + companyId);
  },

  /**
   * Invalidate all caches for a report
   * @param {string} reportId - Report ID
   */
  invalidateReportCache: (reportId) => {
    BrsrCache.remove(BrsrCache.PREFIXES.REPOSITORY_ITEM + reportId);
    BrsrCache.remove(BrsrCache.PREFIXES.LINKS_DATA + reportId);
  },

  /**
   * Get cache statistics
   * @returns {object} Cache stats
   */
  getStats: () => {
    const stats = {
      totalItems: 0,
      expiredItems: 0,
      activeItems: 0,
      items: [],
    };

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("brsr_")) {
        stats.totalItems++;
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (Date.now() > cached.expiresAt) {
            stats.expiredItems++;
          } else {
            stats.activeItems++;
            stats.items.push({
              key,
              createdAt: new Date(cached.createdAt).toLocaleString(),
              expiresAt: new Date(cached.expiresAt).toLocaleString(),
              size: JSON.stringify(cached).length,
            });
          }
        } catch (e) {
          // ignore
        }
      }
    });

    return stats;
  },

  /**
   * Clean expired items
   */
  cleanExpired: () => {
    let cleaned = 0;
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("brsr_")) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (Date.now() > cached.expiresAt) {
            localStorage.removeItem(key);
            cleaned++;
          }
        } catch (e) {
          // ignore
        }
      }
    });
    return cleaned;
  },
};

export default BrsrCache;
